import { Feature, TypedFeatureProperties } from '@vi-iot/client';
import { Unit } from '../../shared/common/units';

export interface TransformedEnergyDetails {
  value: number;
  unit: Unit;
  multiplier: number;
}

enum EnergyType {
  Energy,
  EnergyHours,
}

export function isThisEnergyFeature(featureUnit: Unit): boolean {
  return energyHoursUnits.includes(featureUnit) || energyUnits.includes(featureUnit);
}

const energyHoursUnits = [Unit.WattHour, Unit.KilowattHour, Unit.MegawattHour];
const energyShortHoursUnits = [Unit.ShortWattHour, Unit.ShortKilowattHour, Unit.ShortMegawattHour];
const energyUnits = [Unit.Watt, Unit.Kilowatt, Unit.Megawatt];
const energyShortUnits = [Unit.ShortWatt, Unit.ShortKilowatt, Unit.ShortMegawatt];

export function getTransformedEnergyDetailsFromFeature(
  feature: Feature<TypedFeatureProperties<number>, unknown>,
  propertyName: string
): TransformedEnergyDetails {
  const featureValue = feature.properties[propertyName].value;
  const featureUnit = feature.properties[propertyName].unit;

  return getTransformedEnergyDetailsFromValueAndUnit(featureValue, featureUnit);
}

export function getTransformedEnergyDetailsFromValueAndUnit(
  value: string | number,
  unit: string | Unit
): TransformedEnergyDetails {
  const energyHoursUnitsStringArray = Object.values(
    energyHoursUnits.concat(energyShortHoursUnits)
  ) as string[];
  const energyUnitsStringArray = Object.values(energyUnits.concat(energyShortUnits)) as string[];

  if (typeof value === 'string') {
    value = Number(value);
  }

  if (typeof unit === 'string') {
    unit = unit.trim();
  }

  const isNegative = value < 0;
  const positiveValue = Math.abs(value);
  let transformedEnergyDetails: TransformedEnergyDetails;
  if (energyHoursUnitsStringArray.includes(unit)) {
    transformedEnergyDetails = getTransformedEnergyHoursDetails(positiveValue, unit as Unit);
  } else if (energyUnitsStringArray.includes(unit)) {
    transformedEnergyDetails = getTransformedEnergyDetails(positiveValue, unit as Unit);
  } else {
    transformedEnergyDetails = {
      unit: unit as Unit,
      value: +(Math.round(value * 10) / 10).toFixed(1),
      multiplier: 1,
    };
  }
  if (isNegative) {
    transformedEnergyDetails.value = -Math.abs(transformedEnergyDetails.value);
  }

  return transformedEnergyDetails;
}

/**
 *
 *
 * All Energy Hours units (Wh) should be displayed in a proper format.
 * Display of them should be adjusted accordingly to rules:
 *  ---
 * Transforms kWh values
 * - <1 OR 0 OR null into Wh
 * - 1 - 1000 into kWh
 * - 1000+ into MWH
 *
 * digits rounding
 * - 1-0-0 bigValue = value >= 100
 * - 1-1-1 mediumValue = value < 100 && value >= 10
 * - 1.0-2 smallValue = value < 10 && value >= 1
 * - 1.0-2 verySmallValue = value < 1
 */

// Wh, kWh, MWh
export function getTransformedEnergyHoursDetails(
  featureValue: number,
  featureUnit: Unit
): TransformedEnergyDetails {
  let kiloUnitValue = getValueInKiloUnit(featureValue, featureUnit);
  const { adjustedValue, adjustedUnit, multiplier } = getAdjustedKiloValueDetails(
    kiloUnitValue,
    EnergyType.EnergyHours
  );
  let fixedPrecision = getValuePrecision(adjustedValue, adjustedUnit);

  return {
    value: +(kiloUnitValue * multiplier).toFixed(fixedPrecision),
    unit: adjustedUnit as Unit,
    multiplier,
  };
}

// W, kW, MW
export function getTransformedEnergyDetails(
  featureValue: number,
  featureUnit: Unit
): TransformedEnergyDetails {
  let kiloUnitValue = getValueInKiloUnit(featureValue, featureUnit);
  const { adjustedValue, adjustedUnit, multiplier } = getAdjustedKiloValueDetails(
    kiloUnitValue,
    EnergyType.Energy
  );
  let fixedPrecision = getValuePrecision(adjustedValue, adjustedUnit);

  return {
    value: +(kiloUnitValue * multiplier).toFixed(fixedPrecision),
    unit: adjustedUnit,
    multiplier,
  };
}

export function getValueInKiloUnit(value: number, featureUnit: Unit): number {
  let kiloUnitValue: number;

  switch (featureUnit) {
    case Unit.Watt:
    case Unit.ShortWatt:
    case Unit.WattHour:
    case Unit.ShortWattHour: {
      kiloUnitValue = value / 1000;

      break;
    }
    case Unit.Kilowatt:
    case Unit.ShortKilowatt:
    case Unit.KilowattHour:
    case Unit.ShortKilowattHour: {
      kiloUnitValue = value;

      break;
    }
    case Unit.Megawatt:
    case Unit.ShortMegawatt:
    case Unit.MegawattHour:
    case Unit.ShortMegawattHour: {
      kiloUnitValue = value * 1000;

      break;
    }
  }

  return kiloUnitValue;
}

export function transformValueByUnit(
  value: number,
  initialUnit: Unit,
  transformedUnit: Unit
): number {
  const kiloUnitValue = getValueInKiloUnit(value, initialUnit);
  let valueInNewUnit: number;

  switch (transformedUnit) {
    case Unit.Watt:
    case Unit.ShortWatt:
    case Unit.WattHour:
    case Unit.ShortWattHour: {
      valueInNewUnit = kiloUnitValue * 1000;

      break;
    }
    case Unit.Kilowatt:
    case Unit.ShortKilowatt:
    case Unit.KilowattHour:
    case Unit.ShortKilowattHour: {
      valueInNewUnit = kiloUnitValue;

      break;
    }
    case Unit.Megawatt:
    case Unit.ShortMegawatt:
    case Unit.MegawattHour:
    case Unit.ShortMegawattHour: {
      valueInNewUnit = kiloUnitValue / 1000;

      break;
    }
  }

  return valueInNewUnit;
}

function getAdjustedKiloValueDetails(
  kiloUnitValue: number,
  energyType: EnergyType
): { adjustedUnit: Unit; adjustedValue: number; multiplier: number } {
  const isMW = kiloUnitValue >= 1000;
  const iskW = kiloUnitValue < 1000 && kiloUnitValue >= 1;
  const isW = kiloUnitValue === 0 || kiloUnitValue == null || kiloUnitValue < 1;

  let adjustedUnit = Unit.Kilowatt;
  let multiplier = 1;

  if (isMW) {
    adjustedUnit = energyType === EnergyType.Energy ? Unit.Megawatt : Unit.MegawattHour;
    multiplier = 1 / 1000;
  } else if (iskW) {
    adjustedUnit = energyType === EnergyType.Energy ? Unit.Kilowatt : Unit.KilowattHour;
    multiplier = 1;
  } else if (isW) {
    adjustedUnit = energyType === EnergyType.Energy ? Unit.Watt : Unit.WattHour;
    multiplier = 1000;
  }

  const adjustedValue = kiloUnitValue * multiplier;

  return {
    adjustedValue,
    adjustedUnit,
    multiplier,
  };
}

export function getValuePrecision(value: number, unit: Unit): number {
  let fixedPrecision: number;
  const bigValue = value >= 100;
  const mediumValue = value < 100 && value >= 10;
  const smallValue = value < 10 && value >= 1;
  const verySmallValue = value < 1;

  if (bigValue) {
    fixedPrecision = 0;
  } else if (mediumValue) {
    fixedPrecision = 1;
  } else if (smallValue) {
    fixedPrecision = 2;
  } else if (verySmallValue) {
    // for very big unit smaller values should get additional rounding
    switch (unit) {
      case Unit.Megawatt:
      case Unit.ShortMegawatt:
      case Unit.MegawattHour:
      case Unit.ShortMegawattHour: {
        fixedPrecision = 3;
        break;
      }
      default: {
        fixedPrecision = 2;
      }
    }
  }

  return fixedPrecision;
}
